<template>
  <div
    class="min-h-screen rounded-3xl border border-black/5 dark:border-white/10 bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-50 shadow-apple flex items-stretch justify-center">
    <transition name="page-fade" appear>
      <div class="w-full max-w-6xl mx-auto px-4 py-8 lg:py-10">
        <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6 lg:mb-8 page-header">
          <div>
            <p class="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">
              NexGrid Studio
            </p>
            <h1 class="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight">
              My palette projects
            </h1>
            <p class="mt-1 text-[13px] text-zinc-500 dark:text-zinc-400 max-w-xl">
              Create a project, give it a name and description, then open it to
              work on templates and color variations.
            </p>
          </div>
          <div class="flex items-center gap-3">
            <div class="hidden sm:flex items-center gap-2 text-[11px] text-zinc-500 dark:text-zinc-400 mr-2">
              <span v-if="isLoading">Loading...</span>
              <span v-else>{{ projects.length }} project(s)</span>
            </div>

            <router-link to="/dashboard/hub"
              class="group relative flex items-center justify-center h-10 w-10 rounded-2xl border border-zinc-200/80 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-400 hover:text-sky-500 hover:border-sky-500/30 hover:shadow-lg hover:shadow-sky-500/10 transition-all duration-300 hover:-translate-y-0.5 active:scale-95"
              title="Tutorial Hub">
              <span
                class="material-symbols-rounded text-[20px] group-hover:rotate-12 transition-transform duration-300">school</span>
            </router-link>
            
            <button type="button" @click="openCreateModal"
              class="inline-flex items-center gap-2 rounded-2xl bg-sky-500 px-4 py-2.5 text-xs font-medium text-zinc-950 hover:bg-sky-400 shadow-sm shadow-sky-500/40 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sky-500/60"
              :disabled="isLoading">
              <span class="material-symbols-rounded text-sm">add</span>
              New project
            </button>
          </div>
        </header>

        <main class="space-y-6">
          <transition-group v-if="!isLoading && projects.length" name="projects-grid" tag="div"
            class="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            <button v-for="(project, index) in projects" :key="project.id" type="button" :style="{ '--i': index }"
              @click="openProjectQuickView(project)"
              class="group relative flex flex-col rounded-3xl border border-zinc-200/80 dark:border-zinc-800/80 bg-white/80 dark:bg-zinc-900/80 p-4 text-left shadow-sm hover:shadow-xl hover:border-sky-500/70 transition-all duration-300 hover:-translate-y-1 hover:bg-white dark:hover:bg-zinc-900/90">
              <div class="flex items-start justify-between gap-3 mb-3">
                <div class="flex items-center gap-3">
                  <div
                    class="h-10 w-10 flex-shrink-0 rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-400 text-zinc-950 flex items-center justify-center text-xs font-semibold shadow-[0_8px_20px_rgba(56,189,248,0.55)]">
                    {{ projectInitials(project.name) }}
                  </div>
                  <div class="min-w-0">
                    <p
                      class="text-sm font-medium truncate group-hover:text-sky-600 dark:group-hover:text-sky-300 transition-colors">
                      {{ project.name }}
                    </p>
                    <p class="text-[11px] text-zinc-500 dark:text-zinc-400 line-clamp-1">
                      {{ project.description || 'No description yet.' }}
                    </p>
                  </div>
                </div>
                <button type="button" @click.stop="openCardMenu(project)"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-2xl border border-zinc-200/80 dark:border-zinc-700/80 bg-zinc-50/80 dark:bg-zinc-900/80 text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 hover:border-sky-400/70 hover:bg-sky-50/60 dark:hover:bg-sky-900/30 text-[16px] transition-all">
                  <span class="material-symbols-rounded text-[18px]">more_horiz</span>
                </button>
              </div>

              <div class="mb-4">
                <div
                  class="relative overflow-hidden rounded-2xl border border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-900/5 dark:bg-zinc-50/5">
                  <div class="flex h-24 sm:h-28">
                    <div v-for="(color, idx) in getPreviewColors(project)" :key="idx"
                      class="relative flex-1 transition-transform duration-300 group-hover:scale-[1.02]"
                      :style="{ backgroundColor: color }"></div>
                  </div>
                </div>
                <p class="mt-2 text-[11px] text-zinc-500 dark:text-zinc-400 flex items-center gap-1">
                  <span class="material-symbols-rounded text-[13px] text-sky-500 dark:text-sky-300">palette</span>
                  Project color preview
                </p>
              </div>

              <div
                class="mt-auto flex items-center justify-between gap-3 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                <div class="flex flex-col gap-0.5">
                  <span class="text-[10px] uppercase tracking-[0.14em] text-zinc-400 dark:text-zinc-500">
                    Created on
                  </span>
                  <span class="text-[11px] text-zinc-600 dark:text-zinc-300 tabular-nums">
                    {{ formatDate(project.createdAt) }}
                  </span>
                </div>
                <button type="button"
                  class="inline-flex items-center gap-1.5 rounded-2xl bg-sky-500 px-3 py-1.5 text-[11px] font-medium text-zinc-950 hover:bg-sky-400 hover:shadow-[0_10px_30px_rgba(56,189,248,0.55)] transition-all duration-200 hover:-translate-y-0.5"
                  @click.stop="goToProjectDetail(project)">
                  Open in Studio
                  <span class="material-symbols-rounded text-[14px] leading-none">arrow_outward</span>
                </button>
              </div>

              <div v-if="cardMenuProject && cardMenuProject.id === project.id"
                class="absolute right-4 top-12 z-10 w-40 rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg py-1.5 menu-pop">
                <button type="button"
                  class="flex w-full items-center justify-between px-3 py-1.5 text-[12px] text-zinc-600 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                  @click.stop="openEditModalFromProject(project)">
                  Edit
                  <span class="material-symbols-rounded text-[14px]">edit</span>
                </button>
                <button type="button"
                  class="flex w-full items-center justify-between px-3 py-1.5 text-[12px] text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                  @click.stop="openDeleteModalFromProject(project)">
                  Delete
                  <span class="material-symbols-rounded text-[14px]">delete</span>
                </button>
              </div>
            </button>
          </transition-group>

          <div v-else-if="!isLoading && !projects.length"
            class="flex flex-col items-center justify-center py-16 text-center border border-dashed border-zinc-200 dark:border-zinc-800/80 rounded-3xl bg-zinc-50/70 dark:bg-zinc-950/40 empty-state">
            <div
              class="mb-3 h-12 w-12 rounded-3xl bg-zinc-100 dark:bg-zinc-900 flex items-center justify-center text-zinc-500">
              <span class="material-symbols-rounded text-2xl">palette</span>
            </div>
            <p class="text-sm font-medium mb-1">No projects yet</p>
            <p class="text-[12px] text-zinc-500 dark:text-zinc-400 max-w-xs mb-4">
              Create your first project, define a name and description, then
              open the Studio to build templates and color combinations.
            </p>
            <button type="button" @click="openCreateModal"
              class="inline-flex items-center gap-2 rounded-2xl bg-sky-500 px-4 py-2 text-xs font-medium text-zinc-950 hover:bg-sky-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(56,189,248,0.55)]"
              :disabled="isLoading">
              New project
            </button>
          </div>

          <div v-if="isLoading" class="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            <div v-for="n in 3" :key="n"
              class="animate-pulse rounded-3xl border border-zinc-200/80 dark:border-zinc-800/80 bg-white/70 dark:bg-zinc-900/70 p-4">
              <div class="flex items-center gap-3 mb-3">
                <div class="h-10 w-10 rounded-2xl bg-zinc-200 dark:bg-zinc-800">
                </div>
                <div class="flex-1 space-y-2">
                  <div class="h-3 w-3/4 rounded-full bg-zinc-200 dark:bg-zinc-800"></div>
                  <div class="h-2.5 w-1/2 rounded-full bg-zinc-200 dark:bg-zinc-800"></div>
                </div>
              </div>
              <div
                class="flex h-24 sm:h-28 rounded-2xl overflow-hidden border border-zinc-200/80 dark:border-zinc-800/80 mb-3">
                <div v-for="i in 5" :key="i" class="flex-1 bg-zinc-100 dark:bg-zinc-800"></div>
              </div>
              <div class="h-3 w-1/2 rounded-full bg-zinc-200 dark:bg-zinc-800"></div>
            </div>
          </div>
        </main>
      </div>
    </transition>

    <transition name="overlay-fade">
      <div v-if="showCreateModal"
        class="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
        <div
          class="w-full max-w-md rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 shadow-xl modal-panel">
          <div class="flex items-start justify-between gap-3 mb-3">
            <div>
              <p class="text-[11px] uppercase tracking-[0.16em] text-zinc-400 dark:text-zinc-500 mb-0.5">
                New project
              </p>
              <h2 class="text-sm font-semibold">Create palette project</h2>
            </div>
            <button type="button" @click="closeCreateModal"
              class="h-8 w-8 inline-flex items-center justify-center rounded-2xl bg-zinc-100 dark:bg-zinc-800 text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 transition-colors"
              :disabled="isSaving">
              <span class="material-symbols-rounded text-[18px]">close</span>
            </button>
          </div>

          <div class="space-y-4">
            <div class="space-y-1.5">
              <label class="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">Project name</label>
              <input v-model="newProject.name" type="text" placeholder="Ex: Neon Noir Color Set"
                class="w-full rounded-2xl bg-zinc-50 dark:bg-zinc-950/80 border border-zinc-200 dark:border-zinc-800 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500" />
            </div>
            <div class="space-y-1.5">
              <label class="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">Description</label>
              <textarea v-model="newProject.description" rows="4"
                placeholder="Briefly describe the goal of this palette project."
                class="w-full rounded-2xl bg-zinc-50 dark:bg-zinc-950/80 border border-zinc-200 dark:border-zinc-800 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 resize-none"></textarea>
            </div>
          </div>

          <div class="mt-4 flex items-center justify-between gap-3">
            <p class="text-[11px] text-zinc-500 dark:text-zinc-400">
              After creating, you can open the Studio to pick templates and
              build palettes.
            </p>
            <button type="button" :disabled="!canCreate || isSaving" @click="createProject"
              class="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-4 py-2 text-xs font-medium text-zinc-950 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_10px_28px_rgba(16,185,129,0.55)]">
              <span v-if="isSaving">Creating...</span>
              <span v-else>Create and open</span>
              <span class="material-symbols-rounded text-sm">check</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="overlay-fade">
      <div v-if="showEditModal && selectedProject"
        class="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
        <div
          class="w-full max-w-md rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 shadow-xl modal-panel">
          <div class="flex items-start justify-between gap-3 mb-3">
            <div>
              <p class="text-[11px] uppercase tracking-[0.16em] text-zinc-400 dark:text-zinc-500 mb-0.5">
                Edit project
              </p>
              <h2 class="text-sm font-semibold truncate">
                {{ selectedProject.name }}
              </h2>
            </div>
            <button type="button" @click="closeEditModal"
              class="h-8 w-8 inline-flex items-center justify-center rounded-2xl bg-zinc-100 dark:bg-zinc-800 text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 transition-colors"
              :disabled="isSaving">
              <span class="material-symbols-rounded text-[18px]">close</span>
            </button>
          </div>

          <div class="space-y-4">
            <div class="space-y-1.5">
              <label class="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">Project name</label>
              <input v-model="editProject.name" type="text"
                class="w-full rounded-2xl bg-zinc-50 dark:bg-zinc-950/80 border border-zinc-200 dark:border-zinc-800 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500" />
            </div>
            <div class="space-y-1.5">
              <label class="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">Description</label>
              <textarea v-model="editProject.description" rows="4"
                class="w-full rounded-2xl bg-zinc-50 dark:bg-zinc-950/80 border border-zinc-200 dark:border-zinc-800 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 resize-none"></textarea>
            </div>
          </div>

          <div class="mt-4 flex items-center justify-between gap-3">
            <p class="text-[11px] text-zinc-500 dark:text-zinc-400">
              Changes are reflected inside the Studio for this project.
            </p>
            <button type="button" :disabled="!canEdit || isSaving" @click="updateProject"
              class="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-4 py-2 text-xs font-medium text-zinc-950 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_10px_28px_rgba(16,185,129,0.55)]">
              <span v-if="isSaving">Saving...</span>
              <span v-else>Save</span>
              <span class="material-symbols-rounded text-sm">check</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="overlay-fade">
      <div v-if="showDeleteModal && selectedProject"
        class="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
        <div
          class="w-full max-w-sm rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 shadow-xl modal-panel">
          <div class="flex items-start gap-3">
            <div
              class="mt-0.5 h-8 w-8 flex items-center justify-center rounded-2xl bg-red-50 text-red-500 dark:bg-red-950/40 dark:text-red-400">
              <span class="material-symbols-rounded text-xl">warning</span>
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
                Delete project?
              </h3>
              <p class="mt-1 text-[12px] text-zinc-600 dark:text-zinc-400">
                You are about to delete the project
                <span class="font-medium">"{{ selectedProject.name }}"</span>.
                This action is permanent and will also remove palettes created
                inside it.
              </p>
            </div>
          </div>
          <div class="mt-4 flex items-center justify-between gap-3">
            <button type="button" @click="closeDeleteModal"
              class="inline-flex items-center justify-center h-9 rounded-2xl px-4 text-[12px] font-medium border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              :disabled="isDeleting">
              Cancel
            </button>
            <button type="button" @click="confirmDelete"
              class="inline-flex items-center justify-center h-9 rounded-2xl px-4 text-[12px] font-medium bg-red-500 text-white hover:bg-red-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_10px_26px_rgba(239,68,68,0.6)]"
              :disabled="isDeleting">
              <span v-if="isDeleting">Deleting...</span>
              <span v-else>Delete project</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, reactive, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import authService from '@/service/authService'

const router = useRouter()

const projects = ref([])
const selectedProject = ref(null)
const cardMenuProject = ref(null)

const isLoading = ref(false)
const isSaving = ref(false)
const isDeleting = ref(false)

const showCreateModal = ref(false)
const showEditModal = ref(false)
const showDeleteModal = ref(false)

const newProject = reactive({
  name: '',
  description: ''
})

const editProject = reactive({
  id: null,
  name: '',
  description: ''
})

const canCreate = computed(() => newProject.name.trim().length > 0)
const canEdit = computed(() => editProject.name.trim().length > 0)

function openCreateModal() {
  newProject.name = ''
  newProject.description = ''
  showCreateModal.value = true
}

function closeCreateModal() {
  if (isSaving.value) return
  showCreateModal.value = false
}

function openEditModalFromProject(project) {
  selectedProject.value = project
  editProject.id = project.id
  editProject.name = project.name || ''
  editProject.description = project.description || ''
  cardMenuProject.value = null
  showEditModal.value = true
}

function openProjectQuickView(project) {
  selectedProject.value = project
}

function closeEditModal() {
  if (isSaving.value) return
  showEditModal.value = false
}

function openCardMenu(project) {
  if (cardMenuProject.value && cardMenuProject.value.id === project.id) {
    cardMenuProject.value = null
    return
  }
  cardMenuProject.value = project
}

function openDeleteModalFromProject(project) {
  selectedProject.value = project
  cardMenuProject.value = null
  showDeleteModal.value = true
}

function closeDeleteModal() {
  if (isDeleting.value) return
  showDeleteModal.value = false
}

async function fetchProjects() {
  try {
    isLoading.value = true
    const { data } = await axios.get('/studio/projects')
    projects.value = data || []
  } catch (e) {
    console.error('Error loading projects', e)
  } finally {
    isLoading.value = false
  }
}

async function createProject() {
  if (!canCreate.value || isSaving.value) return
  try {
    isSaving.value = true
    const payload = {
      name: newProject.name.trim(),
      description: newProject.description.trim() || undefined
    }
    const { data } = await axios.post('/studio/projects', payload)
    if (data) {
      projects.value = [data, ...projects.value]
      selectedProject.value = data
      showCreateModal.value = false
      router.push({ name: 'studio-project', params: { projectId: data.id } })
    }
  } catch (e) {
    console.error('Error creating project', e)
  } finally {
    isSaving.value = false
  }
}

async function updateProject() {
  if (!canEdit.value || isSaving.value || !editProject.id) return
  try {
    isSaving.value = true
    const payload = {
      name: editProject.name.trim(),
      description: editProject.description.trim() || undefined
    }
    const { data } = await axios.patch(`/studio/projects/${editProject.id}`, payload)
    if (data) {
      projects.value = projects.value.map(p => (p.id === data.id ? data : p))
      selectedProject.value = data
      showEditModal.value = false
    }
  } catch (e) {
    console.error('Error updating project', e)
  } finally {
    isSaving.value = false
  }
}

async function confirmDelete() {
  if (!selectedProject.value || isDeleting.value) return
  try {
    isDeleting.value = true
    await axios.delete(`/studio/projects/${selectedProject.value.id}`)
    projects.value = projects.value.filter(p => p.id !== selectedProject.value.id)
    selectedProject.value = null
    showDeleteModal.value = false
  } catch (e) {
    console.error('Error deleting project', e)
  } finally {
    isDeleting.value = false
  }
}

function goToProjectDetail(project) {
  const target = project || selectedProject.value
  if (!target) return
  router.push({ name: 'studio-project', params: { projectId: target.id } })
}

function projectInitials(name) {
  if (!name) return 'NG'
  const parts = name.trim().split(' ')
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

function getPreviewColors(project) {
  if (Array.isArray(project.previewColors) && project.previewColors.length) {
    return project.previewColors.slice(0, 5)
  }
  return ['#104E43', '#8CC63F', '#EC5E10', '#FAA41F', '#FFE5B3']
}

function formatDate(date) {
  try {
    const d = new Date(date)
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    })
  } catch {
    return ''
  }
}

onMounted(() => {
  const current = authService.current()
  if (!current) {
    router.push('/')
    return
  }
  fetchProjects()
})
</script>

<style scoped>
.material-symbols-rounded {
  font-family: 'Material Symbols Rounded', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: normal;
  font-style: normal;
  font-size: 1em;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}

.hex-code {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(90deg);
  font-size: 0.65rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(248, 250, 252, 0.96);
  text-shadow: 0 0 10px rgba(15, 23, 42, 0.85);
}

.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

.projects-grid-enter-active,
.projects-grid-leave-active {
  transition: all 0.35s cubic-bezier(0.21, 0.61, 0.35, 1);
}

.projects-grid-enter-active>* {
  transition: inherit;
  transition-property: opacity, transform, box-shadow, border-color, background-color;
  transition-delay: calc(var(--i) * 40ms);
}

.projects-grid-enter-from {
  opacity: 0;
  transform: translateY(14px) scale(0.98);
}

.projects-grid-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}

.menu-pop {
  transform-origin: top right;
  animation: menu-pop-in 0.18s ease-out;
}

@keyframes menu-pop-in {
  from {
    opacity: 0;
    transform: scale(0.92) translateY(-4px);
  }

  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.empty-state {
  animation: empty-fade-in 0.35s ease-out;
}

@keyframes empty-fade-in {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.28s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.modal-panel {
  transform-origin: top center;
}

.overlay-fade-enter-active .modal-panel,
.overlay-fade-leave-active .modal-panel {
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.overlay-fade-enter-from .modal-panel,
.overlay-fade-leave-to .modal-panel {
  opacity: 0;
  transform: translateY(12px) scale(0.97);
}

.shadow-apple {
  box-shadow: 0 18px 55px rgba(15, 23, 42, 0.35);
}
</style>
